/**
 * CellHoverCard — rich, context-aware hover cards for the JobApplicantsTable
 * and the main CandidatesListTable.
 *
 * One component, many "kinds". Each kind renders a compact but information-
 * dense preview specific to the cell type (candidate mini-profile, score
 * breakdown, CTC math, stage journey, red-flag list, ai summary, etc.). Kept
 * as pure components — the parent passes in the row + optional job for
 * context.
 *
 * Ships with a 300ms open delay so cursor sweeps don't spam popovers and a
 * short close delay so the cursor can travel from the trigger to the card.
 */
import { useEffect, useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import {
    Award, Building2, Copy, Mail, MapPin, Phone, Sparkles, Star, TrendingUp,
    AlertTriangle, CheckCircle2, Clock, ExternalLink, Home, Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatCompCurrency, budgetBadgeClass, budgetLabel } from "@/lib/budget";
import AIFeedbackButtons from "@/components/AIFeedbackButtons";

const HOVER_OPEN_MS = 300;
const HOVER_CLOSE_MS = 150;

/**
 * Public API — wrap any table cell with this. Renders `children` unchanged
 * inline and only mounts the hover panel on pointer-in.
 */
export function CellHoverCard({ kind, row, job, children, align = "start", side = "right", className = "" }) {
    const [open, setOpen] = useState(false);
    // Phase 157b — any button inside the hover (e.g. ResumePreviewButton)
    // can dispatch `pw-hovercard-close` to force every live hovercard to
    // snap shut. Avoids the awkward "modal opens WITH its hover still
    // peeking out behind it" case where the resume preview dialog lands.
    useEffect(() => {
        const close = () => setOpen(false);
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("pw-hovercard-close", close);
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("pw-hovercard-close", close);
            window.removeEventListener("keydown", onKey);
        };
    }, []);
    return (
        <HoverCard openDelay={HOVER_OPEN_MS} closeDelay={HOVER_CLOSE_MS} open={open} onOpenChange={setOpen}>
            <HoverCardTrigger asChild>
                <span className={`inline-block ${className}`}>{children}</span>
            </HoverCardTrigger>
            <HoverCardContent
                side={side}
                align={align}
                className="w-96 p-0 border-border shadow-xl rounded-none"
                data-testid={`hovercard-${kind}`}
            >
                <HoverContent kind={kind} row={row} job={job} />
            </HoverCardContent>
        </HoverCard>
    );
}

function HoverContent({ kind, row, job }) {
    switch (kind) {
        case "candidate":   return <CandidateHover row={row} />;
        case "score":       return <ScoreHover row={row} job={job} />;
        case "ctc":         return <CtcHover row={row} job={job} />;
        case "budget":      return <BudgetHover row={row} job={job} />;
        case "stage":       return <StageHover row={row} />;
        case "red_flags":   return <RedFlagsHover row={row} />;
        case "notice":      return <NoticeHover row={row} />;
        case "relocate":    return <RelocateHover row={row} />;
        case "rating":      return <RatingHover row={row} />;
        case "ai_summary":  return <AiSummaryHover row={row} />;
        default: return <div className="p-3 text-xs text-muted-foreground">No preview available.</div>;
    }
}

// ─────────────────────────────── Candidate mini-profile ───────────────────────────────
function CandidateHover({ row }) {
    const c = row?.candidate || {};
    const initials = (c.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
    const skills = c.skills || [];
    return (
        <div className="text-sm">
            <div className="flex items-start gap-3 p-3 border-b border-border bg-secondary/30">
                {c.avatar_url ? (
                    <img src={c.avatar_url} alt="" className="w-12 h-12 border border-border object-cover" />
                ) : (
                    <div className="w-12 h-12 bg-ikb text-white grid place-items-center font-display font-black text-lg">{initials}</div>
                )}
                <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{c.name || "Unknown"}</div>
                    <div className="text-xs text-muted-foreground truncate">
                        {c.current_title ? c.current_title : "—"}
                        {c.current_company ? <> · <span className="text-foreground/80">{c.current_company}</span></> : null}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] font-mono text-muted-foreground">
                        {c.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{c.location}</span>}
                        {c.total_experience_years !== undefined && c.total_experience_years !== null && (
                            <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" />{c.total_experience_years} yrs</span>
                        )}
                    </div>
                </div>
            </div>
            <div className="p-3 space-y-2">
                <div className="flex items-center gap-3 text-[11px] font-mono text-muted-foreground flex-wrap">
                    {c.email && (
                        <button className="inline-flex items-center gap-1 hover:text-ikb" onClick={() => { navigator.clipboard.writeText(c.email); toast.success("Email copied"); }}>
                            <Mail className="w-3 h-3" /> {c.email}
                        </button>
                    )}
                    {c.phone && (
                        <button className="inline-flex items-center gap-1 hover:text-ikb" onClick={() => { navigator.clipboard.writeText(c.phone); toast.success("Phone copied"); }}>
                            <Phone className="w-3 h-3" /> {c.phone}
                        </button>
                    )}
                    {c.linkedin_url && (
                        <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-ikb"><ExternalLink className="w-3 h-3" /> LinkedIn</a>
                    )}
                </div>
                {skills.length > 0 && (
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Skills</div>
                        <div className="flex flex-wrap gap-1">
                            {skills.slice(0, 12).map((s) => (
                                <span key={s} className="text-[10px] font-mono border border-border px-1.5 py-0.5 bg-white">{s}</span>
                            ))}
                            {skills.length > 12 && <span className="text-[10px] text-muted-foreground">+{skills.length - 12} more</span>}
                        </div>
                    </div>
                )}
                {row.created_at && (
                    <div className="text-[10px] font-mono text-muted-foreground pt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Applied {row.created_at.slice(0, 10)}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────── Score breakdown ───────────────────────────────
function ScoreHover({ row, job }) {
    const analysis = row?.analysis || {};
    const matched = analysis.matched_skills || analysis.must_have_analysis?.matched || [];
    const missing = analysis.missing_skills || analysis.must_have_analysis?.missing || [];
    const strengths = analysis.strengths || [];
    const score = row?.match_score || 0;
    const jobSkills = [...(job?.required_skills || []), ...(job?.nice_to_have_skills || [])];
    const total = jobSkills.length || (matched.length + missing.length);
    const pct = total > 0 ? Math.round((matched.length / total) * 100) : 0;

    return (
        <div className="text-sm">
            <div className="p-3 border-b border-border bg-secondary/30 flex items-center gap-3">
                <div className={`text-3xl font-display font-black ${score >= 75 ? "text-emerald-700" : score >= 50 ? "text-amber-600" : "text-rose-600"}`}>{score}</div>
                <div className="flex-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">AI Match score</div>
                    <div className="text-xs">
                        {total > 0
                            ? <>Skills coverage: <b>{matched.length}</b> of {total} ({pct}%)</>
                            : <>No skill inventory available</>}
                    </div>
                </div>
            </div>
            <div className="p-3 space-y-2">
                {matched.length > 0 && (
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Matched skills</div>
                        <div className="flex flex-wrap gap-1">
                            {matched.slice(0, 10).map((s) => (
                                <span key={s} className="text-[10px] font-mono border border-emerald-500 bg-emerald-50 text-emerald-800 px-1.5 py-0.5">{s}</span>
                            ))}
                            {matched.length > 10 && <span className="text-[10px] text-muted-foreground">+{matched.length - 10} more</span>}
                        </div>
                    </div>
                )}
                {missing.length > 0 && (
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-rose-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Missing skills</div>
                        <div className="flex flex-wrap gap-1">
                            {missing.slice(0, 10).map((s) => (
                                <span key={s} className="text-[10px] font-mono border border-rose-400 bg-rose-50 text-rose-800 px-1.5 py-0.5">{s}</span>
                            ))}
                            {missing.length > 10 && <span className="text-[10px] text-muted-foreground">+{missing.length - 10} more</span>}
                        </div>
                    </div>
                )}
                {strengths.length > 0 && (
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" />Top strengths</div>
                        <ul className="list-disc list-inside text-xs text-foreground/80 space-y-0.5">
                            {strengths.slice(0, 3).map((s, i) => <li key={i} className="truncate">{s}</li>)}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────── CTC / Increment math ───────────────────────────────
function CtcHover({ row, job }) {
    const c = row?.candidate || {};
    const sp = c.screening_profile || {};
    const currency = sp.ctc_currency || c.salary_currency || "INR";
    const current = num(sp.current_ctc_annual) ?? num(c.current_salary);
    const desired = num(sp.desired_ctc_annual) ?? num(c.desired_salary_max) ?? num(c.desired_salary_min);
    const increment = current && desired ? Math.round(((desired - current) / current) * 1000) / 10 : sp.desired_increment_pct;
    const jobMin = num(job?.salary_min);
    const jobMax = num(job?.salary_max);

    const IncCls = increment == null ? "" : increment > 60 ? "text-rose-600" : increment > 30 ? "text-amber-600" : "text-emerald-700";
    return (
        <div className="text-sm">
            <div className="p-3 border-b border-border bg-secondary/30">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Compensation snapshot</div>
                <div className="mt-1 font-mono text-xs">
                    Current: <b>{current != null ? formatCompCurrency(current, currency) : "—"}</b>
                    <span className="mx-2 text-muted-foreground">→</span>
                    Desired: <b>{desired != null ? formatCompCurrency(desired, currency) : "—"}</b>
                </div>
            </div>
            <div className="p-3 space-y-2 text-xs">
                <Row label="Increment ask" value={increment != null ? <span className={`font-mono ${IncCls}`}>{increment}%</span> : "—"} />
                <Row label="Currency" value={currency} />
                <Row label="Notice period" value={sp.notice_period_days ?? c.notice_period_days ?? "—"} suffix={sp.notice_period_days || c.notice_period_days ? "days" : ""} />
                {jobMin != null && jobMax != null && (
                    <>
                        <Row label="Job budget range" value={<span className="font-mono">{formatCompCurrency(jobMin, currency)} – {formatCompCurrency(jobMax, currency)}</span>} />
                        {desired != null && (
                            <Row
                                label="Vs job max"
                                value={<span className={`font-mono ${desired > jobMax ? "text-rose-600" : "text-emerald-700"}`}>
                                    {desired > jobMax ? "+" : ""}{Math.round(((desired - jobMax) / jobMax) * 1000) / 10}%
                                </span>}
                            />
                        )}
                    </>
                )}
                {row?.budget_status?.status && (
                    <div className="pt-1">
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${budgetBadgeClass(row.budget_status.status)}`}>
                            {budgetLabel(row.budget_status.status)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────── Budget context ───────────────────────────────
function BudgetHover({ row, job }) {
    const bs = row?.budget_status || {};
    const status = bs.status;
    const c = row?.candidate || {};
    const sp = c.screening_profile || {};
    const currency = sp.ctc_currency || c.salary_currency || "INR";
    const desired = num(sp.desired_ctc_annual) ?? num(c.desired_salary_max) ?? num(c.desired_salary_min);
    const jobMin = num(job?.salary_min);
    const jobMax = num(job?.salary_max);

    return (
        <div className="text-sm">
            <div className="p-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                <span className={`inline-block px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${status ? budgetBadgeClass(status) : "border-border text-muted-foreground"}`}>
                    {status ? budgetLabel(status) : "No budget data"}
                </span>
                <span className="text-[10px] font-mono text-muted-foreground">Budget check</span>
            </div>
            <div className="p-3 space-y-2 text-xs">
                {jobMin != null && jobMax != null ? (
                    <Row label="Job range" value={<span className="font-mono">{formatCompCurrency(jobMin, currency)} – {formatCompCurrency(jobMax, currency)}</span>} />
                ) : (
                    <div className="text-muted-foreground text-[11px]">Job has no salary range configured.</div>
                )}
                <Row label="Candidate ask" value={desired != null ? <span className="font-mono">{formatCompCurrency(desired, currency)}</span> : "—"} />
                {desired != null && jobMax != null && (
                    <Row label="Delta vs max" value={<span className={`font-mono ${desired > jobMax ? "text-rose-600" : "text-emerald-700"}`}>
                        {desired > jobMax ? "+" : ""}{Math.round(((desired - jobMax) / jobMax) * 1000) / 10}%
                    </span>} />
                )}
                {bs.explanation && (
                    <div className="text-[11px] text-muted-foreground border-l-2 border-border pl-2 mt-2">{bs.explanation}</div>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────── Stage journey ───────────────────────────────
function StageHover({ row }) {
    // Fetches audit events on first hover; cached in local state so a second
    // hover on the same row doesn't hit the API twice.
    const [events, setEvents] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (events || !row?.id) return;
            setLoading(true);
            try {
                const { data } = await api.get("/audit", {
                    params: { target_type: "application", target_id: row.id, limit: 50 },
                });
                const stageEvents = (data || []).filter((e) => e.action === "stage_changed");
                if (!cancelled) setEvents(stageEvents);
            } catch { if (!cancelled) setEvents([]); }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [row?.id, events]);

    return (
        <div className="text-sm">
            <div className="p-3 border-b border-border bg-secondary/30">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Stage journey</div>
                <div className="mt-1 text-xs">Current: <b>{row?.stage || "Applied"}</b></div>
            </div>
            <div className="p-3 max-h-56 overflow-y-auto pw-thin-scroll">
                {loading && <div className="text-[11px] text-muted-foreground">Loading…</div>}
                {!loading && (!events || events.length === 0) && (
                    <div className="text-[11px] text-muted-foreground">No stage changes recorded yet.</div>
                )}
                {events && events.length > 0 && (
                    <ol className="relative border-l-2 border-border ml-1 space-y-2">
                        {events.slice(0, 8).map((e) => (
                            <li key={e.id} className="pl-3 relative">
                                <span className="absolute -left-[7px] top-1 w-3 h-3 bg-ikb border-2 border-white" />
                                <div className="text-xs font-medium">{e.from_value || "—"} → {e.to_value || "—"}</div>
                                <div className="text-[10px] text-muted-foreground font-mono">
                                    {e.actor_name || "Someone"} · {(e.created_at || "").slice(0, 16).replace("T", " ")}
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────── Red flags ───────────────────────────────
function RedFlagsHover({ row }) {
    const flags = row?.analysis?.red_flags || [];
    return (
        <div className="text-sm">
            <div className="p-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-destructive" />
                <div className="flex-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">AI red flags</div>
                    <div className="text-xs">{flags.length} flagged</div>
                </div>
                {flags.length > 0 && (
                    <AIFeedbackButtons
                        feature="red_flag"
                        targetType="application"
                        targetId={row?.id}
                        payload={{ count: flags.length }}
                        compact
                    />
                )}
            </div>
            <div className="p-3 max-h-64 overflow-y-auto pw-thin-scroll">
                {flags.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground">No red flags detected — this looks clean.</div>
                ) : (
                    <ul className="space-y-2">
                        {flags.slice(0, 8).map((f, i) => {
                            const text = typeof f === "string" ? f : (f?.text || f?.description || f?.title);
                            const severity = typeof f === "object" ? (f?.severity || "medium") : "medium";
                            return (
                                <li key={i} className="border-l-2 pl-2 border-destructive">
                                    <div className="text-xs">{text}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">Severity: {severity}</div>
                                </li>
                            );
                        })}
                        {flags.length > 8 && <li className="text-[10px] text-muted-foreground">+{flags.length - 8} more…</li>}
                    </ul>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────── Notice period ───────────────────────────────
function NoticeHover({ row }) {
    const c = row?.candidate || {};
    const sp = c.screening_profile || {};
    const days = sp.notice_period_days ?? c.notice_period_days;
    const earliest = sp.earliest_join_date;
    return (
        <div className="text-sm">
            <div className="p-3 border-b border-border bg-secondary/30">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Availability</div>
                <div className="mt-1 text-xs">
                    {days != null ? <>Notice period: <b>{days} days</b></> : "Notice period not disclosed"}
                </div>
            </div>
            <div className="p-3 space-y-2 text-xs">
                <Row label="On notice?" value={sp.on_notice_period || "—"} />
                <Row label="Earliest join" value={earliest || "—"} />
                {days != null && (
                    <div className={`text-[11px] font-mono px-2 py-1 border inline-block ${days <= 15 ? "border-emerald-500 text-emerald-800 bg-emerald-50" : days <= 45 ? "border-amber-500 text-amber-800 bg-amber-50" : "border-rose-500 text-rose-800 bg-rose-50"}`}>
                        {days <= 15 ? "Immediate joiner" : days <= 45 ? "Under 45 days" : "60+ day notice"}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────── Relocate ───────────────────────────────
function RelocateHover({ row }) {
    const c = row?.candidate || {};
    const sp = c.screening_profile || {};
    const ready = sp.ready_to_relocate ?? (c.willing_to_relocate === true ? "Yes" : c.willing_to_relocate === false ? "No" : null);
    return (
        <div className="text-sm">
            <div className="p-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                <Home className="w-4 h-4 text-ikb" />
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Relocation</div>
                    <div className="text-xs">Ready: <b>{ready || "Not stated"}</b></div>
                </div>
            </div>
            <div className="p-3 space-y-2 text-xs">
                <Row label="Current location" value={c.location || "—"} />
                <Row label="Preferred work mode" value={sp.offered_work_mode || sp.previous_work_mode || "—"} />
                {sp.reason_for_job_change && (
                    <div className="text-[11px] text-muted-foreground border-l-2 border-border pl-2 mt-2">
                        <span className="font-mono uppercase tracking-widest text-[9px] block mb-0.5">Reason for change</span>
                        {sp.reason_for_job_change}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────── Recruiter rating ───────────────────────────────
function RatingHover({ row }) {
    const rr = row?.recruiter_rating || {};
    const details = rr.details || rr.entries || [];
    return (
        <div className="text-sm">
            <div className="p-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-500" />
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Team rating</div>
                    <div className="text-xs">
                        {rr.avg ? <>Average: <b>{rr.avg}/5</b> · {rr.count || details.length} ratings</> : "No ratings yet"}
                    </div>
                </div>
            </div>
            <div className="p-3 text-xs">
                {details.length === 0 ? (
                    <div className="text-muted-foreground text-[11px]">
                        Individual ratings aren't captured in the applicants list payload.
                        Open the profile to see per-recruiter feedback.
                    </div>
                ) : (
                    <ul className="space-y-1.5">
                        {details.slice(0, 6).map((d, i) => (
                            <li key={i} className="flex items-center justify-between">
                                <span className="truncate">{d.recruiter_name || d.actor_name || "Recruiter"}</span>
                                <span className="font-mono text-[11px] text-amber-700">{d.rating}/5</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────── AI summary ───────────────────────────────
function AiSummaryHover({ row }) {
    const summary = row?.ai_summary || row?.analysis?.summary || "";
    const overall = row?.analysis?.overall_summary || "";
    const text = overall || summary;
    return (
        <div className="text-sm">
            <div className="p-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-ikb" />
                <div className="flex-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">AI evaluation</div>
                    <div className="text-xs">Full summary</div>
                </div>
                {text && (
                    <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(text); toast.success("Summary copied"); }}
                        className="inline-flex items-center gap-1 h-6 px-1.5 border border-border text-[10px] font-mono uppercase tracking-widest hover:border-ikb hover:text-ikb"
                    >
                        <Copy className="w-3 h-3" /> Copy
                    </button>
                )}
            </div>
            <div className="p-3 text-xs max-h-64 overflow-y-auto pw-thin-scroll leading-relaxed">
                {text || <span className="text-muted-foreground">No AI summary generated yet.</span>}
            </div>
            {text && (
                <div className="px-3 py-2 border-t border-border bg-secondary/20 flex items-center justify-between gap-2">
                    <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Was this useful?</span>
                    <AIFeedbackButtons
                        feature="ai_summary"
                        targetType="application"
                        targetId={row?.id}
                        payload={{ length: text.length }}
                        compact
                    />
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────── Small primitives ───────────────────────────────
function Row({ label, value, suffix }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
            <span className="text-xs">{value ?? "—"}{suffix ? ` ${suffix}` : ""}</span>
        </div>
    );
}

function num(v) {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
}
